/**
 * Shared menu content used by both the hamburger menu and the search overlay.
 * - MenuContentCard: Full card with wrapper (for hamburger menu)
 * - MenuColumnsAndFooter: Inner content only (for search overlay)
 */

import { useId, useState } from "react";
import type {
  Menu,
  Topbar,
  NavigationItem,
  LocalizedTexts,
} from "@/types/layout";
import { UserIcon, GlobeIcon, ChevronDownIcon } from "@/components/icons";
import { NavIcon } from "@/components/icons/navIcons";
import { AccountNavRow } from "@/components/common/AccountNavRow";
import { ThemeToggleRow } from "@/components/common/ThemeToggleRow";
import { t } from "@/utils/translations";
import { resolveHref } from "@/utils/url";
import { buildLoginUrl } from "@/utils/auth";
import { useTheme } from "@/utils/theme";
import {
  COMING_SOON_LABEL,
  SECTION_LABELS,
  SETTINGS_ITEM,
  SIGN_OUT_ITEM,
  THEME_TOGGLE_LABELS,
  WEB_APP_ITEMS,
  accountMenuItemsFor,
  goAccountPath,
  isAccountTab,
  isCurrentAccountTab,
  webAppHref,
  type AccountTab,
} from "@/utils/crossAppMenu";
import styles from "./Header.module.css";

/* ─── Shared Props ──────────────────────────────────────────────────────────── */

interface MenuContentProps {
  menu: Menu;
  topbar: Topbar;
  currentLocale: string;
  localizedTexts: LocalizedTexts;
  onLinkClick: () => void;
  showImage?: boolean;
}

/** Sign-in (guest) or my-account (member) action shown at the top of the menu. */
export interface MenuAccountAction {
  href: string;
  /** Already localized — "Kirjaudu" for guests, "Omat sivut" for members. */
  label: string;
}

/** Country + language row that opens the locale selector. */
export interface MenuLocaleAction {
  /** Country name in the current language, e.g. "Suomi". */
  label: string;
  flagSrc: string | null;
  ariaLabel: string;
  onSelect: () => void;
}

/**
 * What the shared Account section needs from the host: where WeHunt Web is,
 * the member's account page path — null for a guest, whose ACCOUNT items
 * then go through login with the matching /go/ entry route as return path —
 * whether the member is B2B, whose account page carries only a subset of the
 * shared tabs, and the sign-out URL for members who sign out from this menu.
 */
export interface MenuCrossAppLinks {
  webAppUrl: string;
  accountPageHref: string | null;
  isB2b: boolean;
  /** Sign out row at the bottom of the section; omitted for guests and B2B. */
  signOutHref?: string | null;
}

interface MenuContentCardProps extends MenuContentProps {
  /**
   * Rendered only in the hamburger menu, not in the search overlay: a lost
   * first-time visitor opens the menu looking for a way in, so the account
   * action is the first and only filled control in the card.
   */
  account?: MenuAccountAction;
  /**
   * The menu's own pill bar has no country selector, so without this row the
   * control is unreachable for as long as the menu is open.
   */
  locale?: MenuLocaleAction;
  /** The shared Account section, present whenever the host has a menu to link into. */
  crossApp?: MenuCrossAppLinks;
}

/* ─── Full Card (hamburger menu) ────────────────────────────────────────────── */

export function MenuContentCard({
  menu,
  topbar,
  currentLocale,
  localizedTexts,
  onLinkClick,
  showImage = true,
  account,
  locale,
  crossApp,
}: MenuContentCardProps) {
  return (
    <div className={styles.menuContent}>
      {(account || locale) && (
        <div className={styles.menuTopActions}>
          {account && (
            <a
              href={account.href}
              className={styles.menuAccountAction}
              onClick={onLinkClick}
            >
              <UserIcon className={styles.menuActionIcon} />
              <span>{account.label}</span>
            </a>
          )}
          {locale && (
            <button
              type="button"
              className={styles.menuLocaleAction}
              onClick={locale.onSelect}
              aria-label={locale.ariaLabel}
            >
              {locale.flagSrc ? (
                <img
                  src={locale.flagSrc}
                  alt=""
                  aria-hidden="true"
                  width={24}
                  height={24}
                  className={styles.menuActionFlag}
                />
              ) : (
                <GlobeIcon className={styles.menuActionIcon} />
              )}
              <span>{locale.label}</span>
              <ChevronDownIcon className={styles.menuLocaleChevron} />
            </button>
          )}
        </div>
      )}
      {crossApp && (
        <AccountMenuSection
          crossApp={crossApp}
          localizedTexts={localizedTexts}
          onLinkClick={onLinkClick}
        />
      )}
      <MenuColumnsAndFooter
        menu={menu}
        topbar={topbar}
        currentLocale={currentLocale}
        localizedTexts={localizedTexts}
        onLinkClick={onLinkClick}
        showImage={showImage}
      />
    </div>
  );
}

/* ─── Shared Account section ────────────────────────────────────────────────── */

/**
 * Below this width the my-account page hides its sidebar, so the menu is the
 * only way to the account tabs and the section opens expanded. Mirrors the
 * breakpoint in AccountSidebar.module.css and MyAccountPage.module.css.
 */
const SIDEBAR_HIDDEN_QUERY = "(max-width: 1024px)";

function isSidebarHidden(): boolean {
  // jsdom has no matchMedia; treat that as the small-screen case so the
  // section renders its rows.
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia(SIDEBAR_HIDDEN_QUERY).matches;
}

interface AccountMenuSectionProps {
  crossApp: MenuCrossAppLinks;
  localizedTexts: LocalizedTexts;
  onLinkClick: () => void;
}

/**
 * The "Account" category of the menu — the my-account sidebar folded into the
 * hamburger, the way WeHunt Web does it on small screens: an accordion whose
 * body is the WEB APP rows, the ACCOUNT rows (disabled ones included, so the
 * two apps list the same menu) and Settings, with Sign out under a hairline.
 * Items that belong to the other app are plain anchors into it.
 */
function AccountMenuSection({
  crossApp,
  localizedTexts,
  onLinkClick,
}: AccountMenuSectionProps) {
  const [isOpen, setIsOpen] = useState(isSidebarHidden);
  const bodyId = useId();
  // Links into WeHunt Web carry the member's light/dark choice across domains.
  const { theme } = useTheme();

  const themeLabels = {
    dark: t(
      localizedTexts,
      THEME_TOGGLE_LABELS.dark.key,
      THEME_TOGGLE_LABELS.dark.fallback
    ),
    light: t(
      localizedTexts,
      THEME_TOGGLE_LABELS.light.key,
      THEME_TOGGLE_LABELS.light.fallback
    ),
  };

  const accountLabel = t(
    localizedTexts,
    SECTION_LABELS.account.key,
    SECTION_LABELS.account.fallback
  );
  const comingSoonText = t(
    localizedTexts,
    COMING_SOON_LABEL.key,
    COMING_SOON_LABEL.fallback
  );

  const accountHref = (tab: AccountTab) =>
    crossApp.accountPageHref
      ? `${crossApp.accountPageHref}?tab=${tab}`
      : buildLoginUrl({ returnPath: goAccountPath(tab) });

  const isActive = (tab: AccountTab) =>
    crossApp.accountPageHref !== null &&
    typeof window !== "undefined" &&
    isCurrentAccountTab(tab, crossApp.accountPageHref, window.location);

  return (
    <section className={styles.accountSection} aria-label={accountLabel}>
      <button
        type="button"
        className={styles.accountSectionToggle}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{accountLabel}</span>
        <NavIcon
          name={isOpen ? "expandLess" : "expandMore"}
          className={styles.accountSectionChevron}
        />
      </button>
      {isOpen && (
        <div id={bodyId} className={styles.accountSectionBody}>
          {WEB_APP_ITEMS.map((item) => (
            <AccountNavRow
              key={item.id}
              size="lg"
              icon={item.icon}
              label={t(localizedTexts, item.key, item.fallback)}
              href={webAppHref(crossApp.webAppUrl, item.path, theme)}
              onClick={onLinkClick}
            />
          ))}
          <div className={styles.accountSectionEyebrow}>{accountLabel}</div>
          {accountMenuItemsFor(crossApp.isB2b).map((item) => {
            const label = t(localizedTexts, item.labelKey, item.fallback);
            if (!item.enabled || !isAccountTab(item.key)) {
              return (
                <AccountNavRow
                  key={item.key}
                  size="lg"
                  icon={item.icon}
                  label={label}
                  disabled
                  disabledTitle={comingSoonText}
                />
              );
            }
            return (
              <AccountNavRow
                key={item.key}
                size="lg"
                icon={item.icon}
                label={label}
                href={accountHref(item.key)}
                active={isActive(item.key)}
                onClick={onLinkClick}
              />
            );
          })}
          <AccountNavRow
            size="lg"
            icon={SETTINGS_ITEM.icon}
            label={t(localizedTexts, SETTINGS_ITEM.key, SETTINGS_ITEM.fallback)}
            href={webAppHref(crossApp.webAppUrl, SETTINGS_ITEM.path, theme)}
            onClick={onLinkClick}
          />
          <div className={styles.accountSectionFooter}>
            <ThemeToggleRow size="lg" labels={themeLabels} />
            {crossApp.signOutHref && (
              <AccountNavRow
                size="lg"
                icon={SIGN_OUT_ITEM.icon}
                label={t(
                  localizedTexts,
                  SIGN_OUT_ITEM.key,
                  SIGN_OUT_ITEM.fallback
                )}
                href={crossApp.signOutHref}
                onClick={onLinkClick}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── Inner Content (columns + footer, no wrapper) ──────────────────────────── */

export function MenuColumnsAndFooter({
  menu,
  topbar,
  currentLocale,
  localizedTexts,
  onLinkClick,
  showImage = true,
}: MenuContentProps) {
  return (
    <>
      <div className={styles.menuInner}>
        <div className={styles.linkColumns}>
          <MenuColumns
            topItems={menu.topItems}
            currentLocale={currentLocale}
            onLinkClick={onLinkClick}
          />
        </div>
        {showImage && (
          <img
            src="/images/wehunt-menu-image.jpg"
            alt=""
            className={styles.menuImage}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        )}
      </div>
      <AppDownloadFooter
        topbar={topbar}
        currentLocale={currentLocale}
        localizedTexts={localizedTexts}
      />
    </>
  );
}

/* ─── Badge locale helpers ──────────────────────────────────────────────────── */

function getBadgeLanguageCode(locale: string): string {
  const first = locale.toLowerCase().split(/[-_]/)[0];
  return first && /^[a-z]{2}$/.test(first) ? first : "en";
}

function getAppleBadgeSrc(locale: string): string {
  return `/images/badges/apple/appstore-badge-${getBadgeLanguageCode(locale)}.svg`;
}

function getGoogleBadgeSrc(locale: string): string {
  return `/images/badges/google/playstore-badge-${getBadgeLanguageCode(locale)}.svg`;
}

/* ─── App Download Footer ──────────────────────────────────────────────────── */

interface AppDownloadFooterProps {
  topbar: Topbar;
  currentLocale: string;
  localizedTexts: LocalizedTexts;
}

function AppDownloadFooter({
  topbar,
  currentLocale,
  localizedTexts,
}: AppDownloadFooterProps) {
  const hasLinks = topbar.appStoreLink || topbar.playStoreLink;
  if (!hasLinks) return null;

  return (
    <div className={styles.menuFooter}>
      <p className={styles.menuFooterTitle}>
        {t(
          localizedTexts,
          "Tracker.Generic.DownloadTheApp",
          "Download the app"
        )}
      </p>
      <div className={styles.appStoreButtons}>
        {topbar.appStoreLink && (
          <a
            href={topbar.appStoreLink.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={getAppleBadgeSrc(currentLocale)}
              alt={topbar.appStoreLink.name}
              className={styles.appStoreButton}
              onError={(e) => {
                const img = e.currentTarget;
                const fallback = getAppleBadgeSrc("en");
                if (!img.src.endsWith(fallback)) {
                  img.src = fallback;
                }
              }}
            />
          </a>
        )}
        {topbar.playStoreLink && (
          <a
            href={topbar.playStoreLink.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={getGoogleBadgeSrc(currentLocale)}
              alt={topbar.playStoreLink.name}
              className={styles.appStoreButton}
              onError={(e) => {
                const img = e.currentTarget;
                const fallback = getGoogleBadgeSrc("en");
                if (!img.src.endsWith(fallback)) {
                  img.src = fallback;
                }
              }}
            />
          </a>
        )}
      </div>
    </div>
  );
}

/* ─── Menu Columns ───────────────────────────────────────────────────────────── */

interface MenuColumnsProps {
  topItems: NavigationItem[];
  currentLocale: string;
  onLinkClick: () => void;
}

function MenuColumns({
  topItems,
  currentLocale,
  onLinkClick,
}: MenuColumnsProps) {
  return (
    <>
      {topItems.map((item) => (
        <div key={item.link.id} className={styles.linkColumn}>
          <h3 className={styles.columnTitle}>{item.link.name}</h3>
          {item.subItems && item.subItems.length > 0 && (
            <div className={styles.columnLinks}>
              {item.subItems.map((subItem) => (
                <a
                  key={subItem.link.id}
                  href={resolveHref(subItem.link, currentLocale)}
                  className={styles.columnLink}
                  onClick={onLinkClick}
                >
                  {subItem.link.name}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export default MenuContentCard;
